<script setup>
import { PhxVideoPlayerWithConsent } from '@phx-styleguides/als';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { CONSENT_NAMES, useConsent } from '@phoenix/composables/consent';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  videoPath: {
    type: String,
    required: true,
  },
  poster: {
    type: String,
    default: '',
  },
  posterVariants: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const { isConsentAccepted, acceptConsent } = useConsent();

const consentAccepted = isConsentAccepted(CONSENT_NAMES.youtube);

const text = computed(() => t('YoutubeVideo.consentText'));

const poster = computed(() => (consentAccepted.value || !props.poster.includes('img.youtube') ? props.poster : null));
const posterVariants = computed(() =>
  consentAccepted.value || !Object.values(props.posterVariants).some((poster) => poster.includes('img.youtube'))
    ? props.posterVariants
    : {},
);

const video = computed(() => ({
  embed: props.videoPath,
  plyrOptions: {
    autoplay: false,
    controls: [
      'play',
      'progress',
      'current-time',
      'mute',
      'volume',
      'captions',
      'settings',
      'pip',
      'airplay',
      'fullscreen',
    ],
    youtube: {
      cc_load_policy: 1,
      cc_lang_pref: window.Phoenix.laravelLanguage,
    },
  },
  poster: poster.value,
  posterVariants: posterVariants.value,
  playBtnOverlaid: {
    variant: 'primary',
    iconOnly: true,
    icon: {
      start: {
        icon: 'play',
        id: 'play',
        title: t('Accessibility.videoPlay'),
      },
    },
  },
}));

const i18n = computed(() => ({
  giveConsentLabel: t('YoutubeVideo.consentButton.label'),
  privacyPolicyLabel: t('YoutubeVideo.privacyPolicyButton.label'),
}));

const privacyPolicyUrl = computed(() => window.route('privacy_policy'));

const onConsentAccept = () => {
  acceptConsent(CONSENT_NAMES.youtube);
};
</script>

<template>
  <phx-video-player-with-consent
    :id="id"
    :text="text"
    :video="video"
    :consent-accepted="consentAccepted"
    :i18n="i18n"
    :privacy-policy-url="privacyPolicyUrl"
    @consent:accept="onConsentAccept"
  />
</template>
